import { useSearchParams, type NavigateOptions } from "react-router";
import { useCallback, useEffect, useRef } from "react";
import { useLatest } from "./useLatest";

/**
 * Hook to read and update companyId from URL params.
 * URL is the single source of truth for selected company.
 */
export function useCompanyId() {
  const [searchParams, setSearchParams] = useSearchParams();

  const companyIdParam = searchParams.get("companyId");
  const companyId = companyIdParam ? Number(companyIdParam) : null;

  const setCompanyId = useCallback(
    (newCompanyId: number | null, options?: NavigateOptions) => {
      const params = new URLSearchParams(searchParams);

      if (newCompanyId) {
        params.set("companyId", newCompanyId.toString());
      } else {
        params.delete("companyId");
      }
      params.delete("brandId");

      setSearchParams(params, options);
    },
    [searchParams, setSearchParams]
  );

  return { companyId, setCompanyId };
}

/**
 * Selects a page's company in the header once, when the page opens. Picking another company
 * afterwards sticks.
 */
export function useSelectCompany(companyId: number | null) {
  const { companyId: selected, setCompanyId } = useCompanyId();
  const applied = useRef<number | null>(null);

  useEffect(() => {
    if (companyId === null || applied.current === companyId) return;
    applied.current = companyId;
    if (selected !== companyId) setCompanyId(companyId, { replace: true });
  }, [companyId, selected, setCompanyId]);
}

/**
 * Calls onSwitch when the header's company changes while the page is open. A change to the page's own
 * company (useSelectCompany selecting it on load) is not a switch.
 */
export function useCompanySwitch(pageCompanyId: number | null, onSwitch: (companyId: number | null) => void) {
  const { companyId } = useCompanyId();
  const previous = useRef(companyId);
  const latest = useLatest(onSwitch);

  useEffect(() => {
    if (companyId === previous.current) return;
    previous.current = companyId;
    if (companyId !== pageCompanyId) latest.current(companyId);
  }, [companyId, pageCompanyId, latest]);
}
